// Piezas compartidas para notificar honorarios por WhatsApp (prueba y envío masivo):
// cargar los honorarios del período, armar las variables y pedir la imagen al backend.
// La imagen (tabla del "Resumen de Pago") la dibuja el backend en PHP: aquí solo se muestra.
import axiosInstance from "../../config/axiosConfig";
import { formatMoney } from "../../utils/utils";
import { mergeBonosEnResultados } from "../../utils/bonos";
import { factorLiquidoResumen, totalesResumenPago } from "../honorarios/components/ResumenPagoAuxiliar";

export const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
export const TIPOS_HONORARIOS = {
    adelanto: { label: "Adelanto (1ra quincena)", corto: "Adelanto", bonos: "adelanto" },
    fm: { label: "Fin de mes", corto: "Fin de mes", bonos: "fin_mes" },
};

export const mesActual = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const nombreDe = (row) =>
    `${row.nombre_auxiliar || ""} ${row.apepat_auxiliar || ""} ${row.apemat_auxiliar || ""}`.replace(/\s+/g, " ").trim();

const capitalizar = (texto) =>
    String(texto || "").toLocaleLowerCase("es-CL").replace(/(^|[\s-])(\p{L})/gu, (_, sep, letra) => sep + letra.toLocaleUpperCase("es-CL"));

/** Nombre para el mensaje de WhatsApp: primer nombre y apellido paterno, ej. "María González". */
export const nombreMensaje = (row) => {
    const primerNombre = String(row.nombre_auxiliar || "").trim().split(/\s+/)[0] || "";
    return capitalizar(`${primerNombre} ${String(row.apepat_auxiliar || "").trim()}`.trim());
};

/**
 * Celulares de Chile a +569XXXXXXXX (misma regla que EnvioService::normalizarTelefono del backend):
 *   912345678 / 56912345678 / +56 9 1234 5678 → +56912345678
 *   12345678 (8 dígitos, formato antiguo sin el 9) → +56912345678
 * Otros países (+XX...) se dejan tal cual. Devuelve null si no es válido (ej. 10 dígitos).
 */
export const normalizarTelefono = (valor) => {
    const texto = String(valor || "").trim();
    const internacional = texto.startsWith("+") || texto.startsWith("00");
    let digitos = texto.replace(/\D/g, "");
    if (texto.startsWith("00")) digitos = digitos.slice(2);

    let t;
    const nacional = digitos.startsWith("56") ? digitos.slice(2) : (internacional ? null : digitos);
    if (nacional === null) t = `+${digitos}`;
    else if (/^9\d{8}$/.test(nacional)) t = `+56${nacional}`;
    else if (/^\d{8}$/.test(nacional)) t = `+569${nacional}`;
    // Sin "+" y sin forma de celular chileno: no se inventa un código de país.
    else if (internacional || digitos.startsWith("56")) t = `+${digitos}`;
    else return null;
    // Chile debe quedar +569 + 8 dígitos; otros países, largo E.164.
    if (t.startsWith("+56")) return /^\+569\d{8}$/.test(t) ? t : null;
    return /^\+[1-9]\d{7,14}$/.test(t) ? t : null;
};

/** Filas de honorarios del período (las mismas de Honorarios / Honorarios Adelanto, con bonos). */
export const cargarFilasHonorarios = async (periodo, tipo) => {
    const params = tipo === "adelanto" ? { fecha: periodo, adelanto: true } : { fecha: periodo };
    const [rem, bonos] = await Promise.all([
        axiosInstance.get("/acuerdo-mutuo/new-remuneraciones", { params }),
        axiosInstance.get(`/liquidacion/${periodo}/bonos`, { params: { tipo: TIPOS_HONORARIOS[tipo].bonos } })
            .catch(() => ({ data: { data: [] } })),
    ]);
    return mergeBonosEnResultados(rem.data?.data || [], bonos.data?.data || [], []);
};

/**
 * Total a pagar (líquido) y datos para la plantilla:
 * [nombre, período, tipo de pago, líquido, bruto de la boleta, mes].
 * En fin de mes el bruto es el valor de la boleta del Resumen de Pago; en adelanto, el bruto de la 1ra quincena.
 */
export const datosHonorarios = (row, tipo, periodo) => {
    const [anio, mes] = periodo.split("-").map(Number);
    const { totalPagarQ1, totalPagarQ2, totalBoleta, brutoQ1 } = totalesResumenPago(row, tipo, factorLiquidoResumen(null, anio));
    const total = Math.round(tipo === "adelanto" ? totalPagarQ1 : totalPagarQ2);
    const bruto = Math.round(tipo === "adelanto" ? brutoQ1 : totalBoleta);
    const nombreMes = MESES[mes - 1] || "";
    return {
        total,
        bruto,
        // {{mes}} completa "a más tardar el 27 de {{mes}}" de honorarios_fm_sk; este mes el plazo se extiende al 29.
        variables: [nombreMensaje(row), `${nombreMes} ${anio}`, TIPOS_HONORARIOS[tipo].corto, formatMoney(total), formatMoney(bruto),
            `cada mes. Sin embargo, por este mes el plazo es hasta el 29 de ${nombreMes}`],
    };
};

/**
 * Variables de un texto de plantilla, en orden: numeradas ({{1}}, {{2}}...) o con nombre
 * ({{nombre}}, {{total}}...). Meta no permite mezclar ambos tipos en una plantilla.
 */
export const clavesDe = (texto = "") => {
    const claves = [...new Set([...String(texto).matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map((m) => m[1]))];
    return claves.every((c) => /^\d+$/.test(c)) ? claves.sort((a, b) => Number(a) - Number(b)) : claves;
};
export const sonNumeradas = (claves) => claves.every((c) => /^\d+$/.test(c));

// Posición de cada dato de honorarios ([nombre, período, tipo de pago, líquido, bruto, mes]) según el nombre de la variable.
// {{total}} es el bruto de la boleta cuando la plantilla también trae {{liquido}} (honorarios_fm_sk);
// en las plantillas sin {{liquido}} (notificacion_honorarios_skmc) {{total}} sigue siendo lo que se paga.
export const ALIAS_HONORARIOS = {
    nombre: 0, name: 0, auxiliar: 0, periodo: 1, tipo_pago: 2, tipo: 2,
    liquido: 3, total_pagar: 3, total: 4, bruto: 4, monto_bruto: 4, mes: 5,
};

/** Valor de honorarios que corresponde a cada variable de la plantilla. */
export const valoresParaClaves = (claves, datos) => {
    const conLiquido = claves.some((c) => c.toLowerCase() === "liquido");
    return claves.map((clave, i) => {
        const nombre = clave.toLowerCase();
        const posicion = /^\d+$/.test(clave) ? Number(clave) - 1
            : (nombre === "total" && !conLiquido) ? ALIAS_HONORARIOS.liquido
            : (ALIAS_HONORARIOS[nombre] ?? i);
        return datos?.[posicion] ?? "";
    });
};

/**
 * Lo que espera el backend: lista de valores (numeradas) o lista [{nombre, valor}] (con nombre),
 * una entrada por cada aparición en el texto y en su orden: Meta cuenta cada {{periodo}} repetido
 * como un parámetro más (error 132000 si faltan).
 */
export const variablesParaEnvio = (texto, claves, valores) => {
    if (sonNumeradas(claves)) return valores;
    const apariciones = [...String(texto || "").matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map((m) => m[1]);
    return apariciones.map((nombre) => ({ nombre, valor: valores[claves.indexOf(nombre)] ?? "" }));
};

/** Pide una imagen (o PDF) al backend y devuelve una URL local (blob:) para mostrarla. Liberar con URL.revokeObjectURL. */
export const imagenComoUrl = async (ruta, params) => {
    try {
        const { data } = await axiosInstance.get(ruta, { params, responseType: "blob" });
        return URL.createObjectURL(data);
    } catch (error) {
        // Con responseType "blob" el error JSON del backend también llega como blob.
        const texto = await error?.response?.data?.text?.().catch(() => null);
        let mensaje = "No se pudo generar la imagen.";
        try { mensaje = JSON.parse(texto).message || mensaje; } catch { /* sin detalle */ }
        throw new Error(mensaje);
    }
};

/** Imagen del "Resumen de Pago" de una auxiliar, dibujada en PHP (vista previa). */
export const urlImagenHonorarios = (periodo, tipo, auxiliarId) =>
    imagenComoUrl("/envios/honorarios/imagen", { periodo, tipo, auxiliar_id: auxiliarId });

/** Imagen de un mensaje del motor (la enviada, o generada si aún no sale). */
export const urlImagenMensaje = (mensajeId) => imagenComoUrl(`/envios/mensajes/${mensajeId}/imagen`);
